import { onMounted, ref } from 'vue';

import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';

import { getDeviceId } from './deviceId';
import { ensureAndroidNotificationChannels } from './fcmAndroidChannels';
import { getFcmAccessToken } from './fcmAccessToken';
import {
	clearPersistedFcmToken,
	ensureFcmTokenSyncVersion,
	persistRegisteredFcmToken,
} from './fcmTokenSync';
import { registerFcmToken, unregisterFcmToken } from './keycloakFcmTokens';

const currentToken = ref<string | null>(null);
let registrationListener: PluginListenerHandle | null = null;
let registerInFlight: Promise<void> | null = null;

const FAKE_FCM_ENABLED =
	typeof import.meta !== 'undefined' &&
	(import.meta as unknown as Record<string, Record<string, string>>).env?.VITE_FAKE_FCM === 'true';

async function resolveFcmToken(): Promise<string | null> {
	const { FCM } = await import('@capacitor-community/fcm');
	const { token } = await FCM.getToken();

	return token ?? null;
}

async function syncTokenToKeycloak(token: string, platform: string): Promise<void> {
	const accessToken = getFcmAccessToken();
	if (!accessToken) {
		console.warn('[FCM] No access token available, skipping register');
		return;
	}

	const deviceId = await getDeviceId();
	await registerFcmToken(accessToken, {
		token,
		platform,
		device_id: deviceId,
	});
	currentToken.value = token;
	await persistRegisteredFcmToken(token);
}

async function registerFake(): Promise<void> {
	const accessToken = getFcmAccessToken();
	if (!accessToken) {
		console.warn('[FCM-FAKE] No access token available, skipping');
		return;
	}

	const deviceId = await getDeviceId();
	const fakeToken = `fake-fcm-${deviceId}`;
	if (currentToken.value === fakeToken) {
		return;
	}

	console.log('[FCM-FAKE] Registering dummy token:', fakeToken);
	await registerFcmToken(accessToken, {
		token: fakeToken,
		platform: 'web',
		device_id: deviceId,
	});
	currentToken.value = fakeToken;
	await persistRegisteredFcmToken(fakeToken);
	console.log('[FCM-FAKE] Dummy token registered on Keycloak SPI');
}

async function registerInternal(): Promise<void> {
	if (FAKE_FCM_ENABLED) {
		await registerFake();
		return;
	}

	const platform = Capacitor.getPlatform();
	if (!Capacitor.isNativePlatform()) {
		return;
	}
	if (platform !== 'android' && platform !== 'ios') {
		return;
	}

	const accessToken = getFcmAccessToken();
	if (!accessToken) {
		console.warn('[FCM] No access token available, skipping register');
		return;
	}

	try {
		const { migrated } = await ensureFcmTokenSyncVersion(async (oldToken) => {
			await unregisterFcmToken(accessToken, oldToken);
		});

		if (migrated) {
			currentToken.value = null;
		}

		const { PushNotifications } = await import('@capacitor/push-notifications');

		let perm = await PushNotifications.checkPermissions();
		if (perm.receive === 'granted') {
			/* already granted */
		} else if (perm.receive !== 'denied') {
			perm = await PushNotifications.requestPermissions();
			if (perm.receive !== 'granted') {
				return;
			}
		} else {
			return;
		}

		await ensureAndroidNotificationChannels(PushNotifications, platform);

		await PushNotifications.register();

		const token = await resolveFcmToken();
		if (token) {
			await syncTokenToKeycloak(token, platform);
		}

		if (registrationListener) {
			await registrationListener.remove();
			registrationListener = null;
		}

		registrationListener = await PushNotifications.addListener('registration', async () => {
			const latestAccessToken = getFcmAccessToken();
			if (!latestAccessToken) {
				return;
			}

			try {
				const refreshedToken = await resolveFcmToken();
				if (!refreshedToken) {
					return;
				}

				if (refreshedToken === currentToken.value) {
					return;
				}

				await syncTokenToKeycloak(refreshedToken, platform);
			} catch (err) {
				console.warn('[FCM] Token refresh registration failed:', err);
			}
		});

		PushNotifications.addListener('registrationError', (err) => {
			console.warn('FCM registration error:', err);
		});
	} catch (err) {
		console.warn('FCM setup failed:', err);
	}
}

async function register(): Promise<void> {
	if (registerInFlight) {
		return registerInFlight;
	}

	registerInFlight = registerInternal().finally(() => {
		registerInFlight = null;
	});

	return registerInFlight;
}

async function unregister(): Promise<void> {
	const token = currentToken.value;
	if (!token) {
		return;
	}

	const accessToken = getFcmAccessToken();
	if (accessToken) {
		try {
			await unregisterFcmToken(accessToken, token);
		} catch (err) {
			console.warn('FCM token delete failed:', err);
		}
	}

	currentToken.value = null;
	await clearPersistedFcmToken();

	if (Capacitor.isNativePlatform()) {
		try {
			const { FCM } = await import('@capacitor-community/fcm');
			await FCM.deleteInstance();
		} catch {
			/* best effort */
		}
	}

	if (registrationListener) {
		await registrationListener.remove();
		registrationListener = null;
	}
}

/** FCM facade: call from anywhere (e.g. notification preferences, logout). */
export const fcmToken = { register, unregister };

/**
 * Vue composable for FCM push token registration.
 * Call from a component that has access to the access-token lifecycle.
 */
export function useFcmToken(opts: { handleMount?: boolean } = {}) {
	const { handleMount = true } = opts;

	if (handleMount) {
		onMounted(() => {
			register();
		});
	}

	return { register, unregister };
}
